"use client";
import { POST_BY_SLUG_QUERYResult } from "@/sanity/types";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import { useRouter } from "next/navigation";

const AddComment = (props: NonNullable<POST_BY_SLUG_QUERYResult>) => {
  const { emojiGroup, _id, slug } = props;
  const { themeColors } = useTheme();
  const router = useRouter();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
    clearErrors, // get clearErrors from useForm
  } = useForm();
  const [selectedEmoji, setSelectedEmoji] = useState<string>("");
  const commentValue = watch("comment", "");

  const onSubmit = async (data: { emoji: string; comment: string }) => {
    const { emoji, comment } = data;
    const response = await fetch("/api/comment", {
      method: "POST",
      body: JSON.stringify({ emoji, comment, postId: _id, slug }),
    });

    if (!response.ok) {
      return console.error("Failed to create comment");
    }
    reset();
    setSelectedEmoji("");
    router.refresh();
  };

  const handleEmojiClick = (emoji: string) => {
    setSelectedEmoji(emoji);
    setValue("emoji", emoji);
    clearErrors("emoji");
  };
  return (
    <div
      className={`z-2 mt-10 md:border md:border-gray-300 rounded-[20px] md:fixed md:bottom-[12px] md:right-[12px] md:p-4 md:w-[500px] backdrop-blur-[5px] ${themeColors.commentLeaveSectionBackgroundColor}`}
    >
      <div
        className={`text-medium md:text-large font-bold ${themeColors.commentTitleColor}`}
      >
        LEAVE A COMMENT
      </div>
      <form
        className="mt-4 flex flex-col"
        onSubmit={handleSubmit((data) =>
          onSubmit(data as { emoji: string; comment: string })
        )}
      >
        <div className="mb-4">
          <label
            className={`block text-sm font-medium ${themeColors.commentLabelColor} mb-1`}
          >
            Choose an emoji
          </label>
          <input type="hidden" {...register("emoji", { required: true })} />
          <div className="flex flex-wrap gap-2">
            {emojiGroup.map((emoji, index) => {
              return (
                <div
                  key={`emojis-${index}`}
                  onClick={() => handleEmojiClick(emoji || "")}
                  className={`cursor-pointer flex items-center justify-center text-[28px]  transition-all ${
                    selectedEmoji === emoji
                      ? themeColors.commentEmojiSelectedColor
                      : ""
                  } px-[6px] rounded-[4px] w-[40px]`}
                >
                  {emoji}
                </div>
              );
            })}
          </div>
          {errors.emoji && (
            <div className="py-1 text-xsmall text-red-600">
              Please select an emoji
            </div>
          )}
        </div>
        <div className="">
          <div className="flex justify-between items-center mb-1">
            <label
              htmlFor="comment"
              className={`block text-sm font-medium ${themeColors.commentLabelColor}`}
            >
              Comment
            </label>
            <span
              className={`text-xsmall ${
                (commentValue?.length || 0) > 500
                  ? "text-red-600"
                  : themeColors.commentLabelColor
              }`}
            >
              {commentValue?.length || 0}/500
            </span>
          </div>
          <textarea
            {...register("comment", {
              required: true,
              minLength: 2,
              maxLength: 500,
            })}
            id="comment"
            className={`block w-full p-2 rounded-md ${themeColors.commentBorderColor} border-1 ring-0 focus:ring-0 focus:outline-none focus:border-2 focus:${themeColors.commentFocusBorderColor}`}
          />
          {errors.comment && (
            <div className="py-1 text-xsmall text-red-600">
              Minimum 2 characters & Maximum 500 characters
            </div>
          )}
        </div>
        <input
          disabled={isSubmitting}
          className={`cursor-pointer mt-4 border ${themeColors.commentButtonBorderColor} ${themeColors.commentButtonColor} px-4 py-2 rounded-md ${isSubmitting ? "opacity-50" : ""}`}
          type="submit"
          value={isSubmitting ? "Submitting..." : "Submit"}
        />
      </form>
    </div>
  );
};

export default AddComment;
